import {
  IFilterComp,
  IFilterParams,
  IDoesFilterPassParams,
} from '@ag-grid-community/core';
import { IssuerRating } from '../../types';
import {
  COMBINED_RATINGS_CHANGE_EVENT,
  CombinedRatings,
  CombinedRatingsChangeDetail,
} from './combined-ratings/combined-ratings';

CombinedRatings;
import {
  getCombinedIssuerCreditRatingFilterOptions,
  getRowRatingLabels,
  getSeriesRatingLabels,
  orderCombinedRatingFilterValues,
  rowMatchesCombinedSeriesRatingFilter,
  SeriesRatingRow,
} from './deals.util';

export interface CombinedRatingFilterParams extends IFilterParams {
  getIssuerRatings: () => IssuerRating | undefined;
  getSeriesByDealId?: () => Map<string, SeriesRatingRow[]> | undefined;
}

/** Matches AG Grid set-filter pill handling */
export interface CombinedRatingFilterModel {
  filterType: 'set';
  values: string[];
}

export class CombinedRatingFilter implements IFilterComp {
  private params!: CombinedRatingFilterParams;
  private filterChangedCallback!: () => void;
  private allOptions: string[] = [];
  /** Allowed rating labels (checked = may appear); row shows if any of M/S/F/K is in this set */
  private allowed: Set<string> = new Set();
  private combinedRatingsEl!: CombinedRatings;
  private onCombinedRatingsChangeBound?: EventListener;

  init(params: IFilterParams): void {
    this.params = params as CombinedRatingFilterParams;
    this.filterChangedCallback = params.filterChangedCallback;
    const issuerRatings = this.params.getIssuerRatings?.();
    const labelsFromGrid = this.collectMappedLabelsFromCurrentGrid(issuerRatings);
    this.allOptions = getCombinedIssuerCreditRatingFilterOptions(issuerRatings, labelsFromGrid);
    this.allowed = new Set(this.allOptions);

    this.combinedRatingsEl = document.createElement('combined-ratings') as CombinedRatings;
    this.combinedRatingsEl.options = [...this.allOptions];
    this.combinedRatingsEl.selectedValues = [...this.allOptions];
    this.onCombinedRatingsChangeBound = (event: Event) => {
      const detail = (event as CustomEvent<CombinedRatingsChangeDetail>).detail;
      this.allowed = new Set(detail.selectedValues);
      this.filterChangedCallback();
    };
    this.combinedRatingsEl.addEventListener(
      COMBINED_RATINGS_CHANGE_EVENT,
      this.onCombinedRatingsChangeBound,
    );
  }

  /** Distinct mapped labels from every loaded row so the set matches what appears in the grid (e.g. VAR). */
  private collectMappedLabelsFromCurrentGrid(
    issuerRatings: IssuerRating | undefined,
  ): Set<string> {
    const distinctMappedLabels = new Set<string>();
    if (!issuerRatings || !this.params.api) {
      return distinctMappedLabels;
    }
    const seriesByDealId = this.params.getSeriesByDealId?.();
    this.params.api.forEachNode((node) => {
      const rowData = node.data;
      if (!rowData) {
        return;
      }
      const dealId = rowData.DEAL_ID != null ? String(rowData.DEAL_ID) : undefined;
      const seriesList = dealId ? seriesByDealId?.get(dealId) : undefined;
      const labels = seriesList?.length
        ? getSeriesRatingLabels(seriesList, issuerRatings)
        : getRowRatingLabels(rowData, issuerRatings);
      for (const mappedLabel of labels) {
        distinctMappedLabels.add(mappedLabel);
      }
    });
    return distinctMappedLabels;
  }

  getGui(): HTMLElement {
    return this.combinedRatingsEl;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (this.allowed.size === 0) {
      return false;
    }
    const issuerRatings = this.params.getIssuerRatings?.();
    if (!issuerRatings || !params.data) {
      return true;
    }
    return rowMatchesCombinedSeriesRatingFilter(
      this.allowed,
      params.data.DEAL_ID,
      this.params.getSeriesByDealId?.() ?? new Map(),
      params.data,
      issuerRatings,
    );
  }

  isFilterActive(): boolean {
    return this.allOptions.length > 0 && this.allowed.size !== this.allOptions.length;
  }

  getModel(): CombinedRatingFilterModel | null {
    if (!this.isFilterActive()) {
      return null;
    }
    const values = orderCombinedRatingFilterValues(
      Array.from(this.allowed),
      this.params.getIssuerRatings?.(),
    );
    return { filterType: 'set', values };
  }

  setModel(model: CombinedRatingFilterModel | null): void {
    if (!model?.values?.length) {
      this.allowed = new Set(this.allOptions);
    } else {
      this.allowed = new Set(model.values);
    }
    if (this.combinedRatingsEl) {
      this.combinedRatingsEl.selectedValues = Array.from(this.allowed);
      this.combinedRatingsEl.clearSearch();
    }
  }

  destroy(): void {
    if (this.combinedRatingsEl && this.onCombinedRatingsChangeBound) {
      this.combinedRatingsEl.removeEventListener(
        COMBINED_RATINGS_CHANGE_EVENT,
        this.onCombinedRatingsChangeBound,
      );
    }
    this.combinedRatingsEl.remove();
  }
}
